import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import api from "../../utils/api";
import { AdminPageHeader } from "../../components/admin/AdminPageShell";

type CaseSnippet = {
  id: string;
  caseId: string;
  type: string;
  status: string;
  progressPercent: number;
  currentFile: string | null;
  isRush: boolean;
  simpleError: string | null;
  filesDone: number;
  filesTotal: number;
  bytesPerSec: number;
};

type MachineStatus = {
  machineId: string;
  machineLabel?: string;
  reportedAt: number;
  receivedAt: number;
  ageMs: number;
  online: boolean;
  health: "online" | "downloading" | "stuck" | "paused" | "offline";
  downloadsPaused: boolean;
  hostedLockout: boolean;
  cycleRunning: boolean;
  cycleRuntimeMs: number;
  summary: {
    total: number;
    downloading: number;
    completed: number;
    failed: number;
    needsManual: number;
    queued: number;
  };
  activeCases: CaseSnippet[];
  stuckOrFailed: CaseSnippet[];
  smart?: { enabled: boolean; mode: string };
};

type StatusResponse = {
  success: boolean;
  serverTime: number;
  offlineAfterMs: number;
  pollHintMs: number;
  machines: MachineStatus[];
};

const healthStyles: Record<MachineStatus["health"], string> = {
  online: "bg-emerald-50 text-emerald-800 ring-emerald-200",
  downloading: "bg-sky-50 text-sky-800 ring-sky-200",
  stuck: "bg-amber-50 text-amber-900 ring-amber-200",
  paused: "bg-orange-50 text-orange-800 ring-orange-200",
  offline: "bg-red-50 text-red-800 ring-red-200",
};

function formatAge(ms: number) {
  if (ms < 1000) return "just now";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s ago`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m ago`;
}

function formatSpeed(bps: number) {
  if (!bps || bps <= 0) return "—";
  if (bps < 1024) return `${Math.round(bps)} B/s`;
  if (bps < 1024 * 1024) return `${(bps / 1024).toFixed(1)} KB/s`;
  return `${(bps / (1024 * 1024)).toFixed(1)} MB/s`;
}

const LocalDownloaderStatus = () => {
  const [data, setData] = useState<StatusResponse | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/auth/admin/local-downloader-status");
      setData(res.data);
      setError("");
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          "Could not load Local Downloader status",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    // UI refresh ≥ 30s — matches Local heartbeat minimum.
    const t = setInterval(() => {
      void load();
    }, 30_000);
    return () => clearInterval(t);
  }, [load]);

  const machines = data?.machines || [];

  return (
    <>
      <Helmet>
        <title>Local Downloader · Admin</title>
      </Helmet>

      <div className="flex flex-col gap-4 w-full min-w-0">
        <AdminPageHeader
          title="Local Downloader"
          subtitle="Live health from the office downloader (updates every 30s)"
        >
          <button
            type="button"
            onClick={() => {
              setLoading(true);
              void load();
            }}
            className="rounded-lg bg-white/15 hover:bg-white/25 text-white text-sm px-4 py-2"
          >
            Refresh
          </button>
        </AdminPageHeader>

        {error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 text-red-800 px-4 py-3 text-sm">
            {error}
          </div>
        ) : null}

        {loading && !data ? (
          <div className="text-sm text-[#8A8A85] px-1">Loading…</div>
        ) : null}

        {!loading && machines.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-[#E8E8E3] bg-white px-6 py-10 text-center">
            <p className="text-sm font-medium text-[#4A4A48]">
              No downloader reporting yet
            </p>
            <p className="text-xs text-[#8A8A85] mt-1 max-w-md mx-auto">
              Start Local Downloader on the office machine. It sends a
              heartbeat to the portal every 30 seconds.
            </p>
          </div>
        ) : null}

        <div className="flex flex-col gap-4">
          {machines.map((m) => (
            <section
              key={m.machineId}
              className="rounded-2xl border border-[#E8E8E3] bg-white overflow-hidden"
            >
              <div className="px-4 py-3 md:px-5 border-b border-[#F0F0EB] flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-base font-semibold text-[#4A4A48] truncate">
                      {m.machineLabel || m.machineId}
                    </h2>
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ring-1 ring-inset ${healthStyles[m.health]}`}
                    >
                      {m.health}
                    </span>
                  </div>
                  <p className="text-xs text-[#8A8A85] mt-1">
                    {m.machineId} · last heartbeat {formatAge(m.ageMs)}
                    {m.smart
                      ? ` · smart ${m.smart.enabled ? m.smart.mode : "off"}`
                      : ""}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2 text-xs text-[#4A4A48]">
                  {m.downloadsPaused ? (
                    <span className="rounded-md bg-orange-50 px-2 py-1">
                      Downloads paused
                    </span>
                  ) : null}
                  {m.hostedLockout ? (
                    <span className="rounded-md bg-red-50 px-2 py-1">
                      Hosted lockout
                    </span>
                  ) : null}
                  {m.cycleRunning ? (
                    <span className="rounded-md bg-sky-50 px-2 py-1">
                      Cycle{" "}
                      {m.cycleRuntimeMs > 0
                        ? `${Math.round(m.cycleRuntimeMs / 1000)}s`
                        : "running"}
                    </span>
                  ) : (
                    <span className="rounded-md bg-gray-50 px-2 py-1">
                      Idle cycle
                    </span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-[#F0F0EB]">
                {[
                  ["Downloading", m.summary.downloading],
                  ["Queued", m.summary.queued],
                  ["Completed", m.summary.completed],
                  ["Failed", m.summary.failed],
                  ["Stuck / manual", m.summary.needsManual],
                  ["Tracked", m.summary.total],
                ].map(([label, value]) => (
                  <div
                    key={String(label)}
                    className="bg-white px-4 py-3 text-center"
                  >
                    <div className="text-lg font-semibold text-[#4A4A48]">
                      {value}
                    </div>
                    <div className="text-[11px] uppercase tracking-wide text-[#8A8A85]">
                      {label}
                    </div>
                  </div>
                ))}
              </div>

              {m.activeCases?.length > 0 ? (
                <div className="px-4 md:px-5 py-3 border-t border-[#F0F0EB]">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-[#8A8A85] mb-2">
                    Active downloads
                  </h3>
                  <ul className="flex flex-col gap-2">
                    {m.activeCases.map((c) => (
                      <li
                        key={c.id}
                        className="rounded-lg border border-[#E8E8E3] px-3 py-2 text-sm"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="font-medium text-[#4A4A48]">
                            {c.isRush ? "⚡ " : ""}
                            {c.caseId}{" "}
                            <span className="text-[#8A8A85] font-normal">
                              ({c.type})
                            </span>
                          </span>
                          <span className="text-xs text-[#8A8A85]">
                            {c.status} · {c.progressPercent}% ·{" "}
                            {c.filesDone}/{c.filesTotal || "?"} ·{" "}
                            {formatSpeed(c.bytesPerSec)}
                          </span>
                        </div>
                        {c.currentFile ? (
                          <p className="text-xs text-[#8A8A85] mt-1 truncate">
                            {c.currentFile}
                          </p>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {m.stuckOrFailed?.length > 0 ? (
                <div className="px-4 md:px-5 py-3 border-t border-[#F0F0EB] bg-amber-50/40">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-amber-900/70 mb-2">
                    Stuck / failed
                  </h3>
                  <ul className="flex flex-col gap-2">
                    {m.stuckOrFailed.map((c) => (
                      <li
                        key={c.id}
                        className="rounded-lg border border-amber-200 bg-white px-3 py-2 text-sm"
                      >
                        <div className="font-medium text-[#4A4A48]">
                          {c.caseId}{" "}
                          <span className="text-[#8A8A85] font-normal">
                            ({c.type} · {c.status})
                          </span>
                        </div>
                        {c.simpleError ? (
                          <p className="text-xs text-amber-900/80 mt-1">
                            {c.simpleError}
                          </p>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </section>
          ))}
        </div>
      </div>
    </>
  );
};

export default LocalDownloaderStatus;
